// WindowedCandidateViewer.tsx
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { ErrorBoundary } from '@sentry/react';

import Box from '@mui/material/Box';
import { Stack } from '@mui/system';
import { Button, Typography, CircularProgress } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/store/store';
import { useGetOrganizationCandidateQuery } from 'src/services/candidates/candidates.service';

import DraggableWindow from 'src/components/draggable-window/draggable-window';

import CandidateDetail from 'src/sections/candidates/view/candidate-detail-view';

interface Props {
  onClose: () => void;
}

export default function WindowedCandidateViewer({ onClose }: Props) {

  const { t } = useTranslate();

  const selectedCandidateId = useAppSelector((state) => state.app.candidateViewer.candidateId);

  const { currentData: orgCandidate, isFetching: isLoading } = useGetOrganizationCandidateQuery(
    selectedCandidateId as string,
    {
      skip: !selectedCandidateId,
    }
  );

  return (
    <DraggableWindow
      onClose={onClose}
      title={isLoading ? 'Loading...' : `${orgCandidate?.candidate_profile.first_name  } ${  orgCandidate?.candidate_profile.last_name}`}
      url={paths.dashboard.candidates.detail(selectedCandidateId as string)}
      maxWidth='50vw'
      adjustable
      maxHeight='80vh'
    >
      <Box minWidth="30vw" minHeight="40vh" p={2}>

        {isLoading && (
          <Stack justifyContent="center" alignItems="center" minWidth="30vw" minHeight="40vh">
            <CircularProgress />
          </Stack>
        )}

        {/*
          The candidate detail page is the whole profile screen rendered inside this window, so a
          throw anywhere in it used to reach the dashboard-wide boundary and replace the entire app
          with the error page — with no history entry to go back to (JT1-1726). Containing it here
          keeps the failure inside the window and leaves the page behind usable.
        */}
        {(!isLoading && orgCandidate) && (
          <ErrorBoundary
            fallback={
              <Stack spacing={2} justifyContent="center" alignItems="center" minWidth="30vw" minHeight="40vh" textAlign="center">
                <Typography variant="subtitle1">{t('candidates.viewer.load_error_title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('candidates.viewer.load_error_message')}
                </Typography>
                <Button variant="contained" onClick={onClose}>
                  {t('candidates.viewer.load_error_close')}
                </Button>
              </Stack>
            }
          >
            <CandidateDetail overrideCandidateId={selectedCandidateId} />
          </ErrorBoundary>
        )}

      </Box>
    </DraggableWindow>
  );
}
